// src/modules/cmt-pelotao/CmtPelotaoDashboard.js
import React, { useState, useEffect, useMemo } from 'react';
import { db, paths } from '../../firebase/config';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { useAuth } from '../../context/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { formatarData, getNormalizedDateString } from '../../utils/helpers';
import CadeteMap from '../../components/CadeteMap';

// Ícones
const CalendarIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
const FileTextIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>;
const CheckCircleIcon = () => <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const ListIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>;
const MapIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>;
const FireIcon = () => <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" /></svg>;
const EyeIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>;

const ratingQuestions = [
    { label: 'Disciplina', name: 'disciplina' },
    { label: 'Pontualidade', name: 'pontualidade' },
    { label: 'Apresentação Pessoal', name: 'apresentacao_pessoal' },
    { label: 'Interação com a Tropa', name: 'interacao_tropa' },
    { label: 'Interação com o Público', name: 'interacao_publico' },
    { label: 'Responsabilidade', name: 'responsabilidade' },
    { label: 'Dedicação e Interesse', name: 'dedicacao_interesse' },
    { label: 'Iniciativa', name: 'iniciativa' },
    { label: 'Capacidade de Decisão', name: 'capacidade_decisao' },
    { label: 'Comprometimento Institucional', name: 'comprometimento_instituicao' },
    { label: 'Traços de Liderança', name: 'tracos_lideranca' },
    { label: 'Conhecimento Técnico', name: 'conhecimento_tecnico' },
    { label: 'Estabilidade Emocional', name: 'estabilidade_emocional' },
    { label: 'Conhecimento Jurídico', name: 'conhecimento_juridico' },
    { label: 'Autoconfiança', name: 'autoconfianca' },
];

const queimogramaQuestions = [
    { label: 'Conhecimento Técnico', name: 'conhecimento_tecnico' },
    { label: 'Pontualidade', name: 'pontualidade' },
    { label: 'Apresentação Pessoal', name: 'apresentacao_pessoal' },
    { label: 'Interação com a Tropa', name: 'interacao_tropa' },
    { label: 'Dedicação ao Ensino', name: 'dedicacao_ensino' },
    { label: 'Estabilidade Emocional', name: 'estabilidade_emocional' },
    { label: 'Capacidade de Decisão', name: 'capacidade_decisao' },
    { label: 'Ética e Compromisso', name: 'etica_compromisso' },
    { label: 'Liderança', name: 'lideranca' },
    { label: 'Nota Geral', name: 'nota_geral' },
];

export const CmtPelotaoDashboard = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);

  const [escolhas, setEscolhas] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [queimogramas, setQueimogramas] = useState([]);
  const [allLocais, setAllLocais] = useState([]); 

  const [activeTab, setActiveTab] = useState('escala'); 
  const [filtroPelotao, setFiltroPelotao] = useState('todos'); // NOVO: Estado do filtro
  
  const [qFilterNome, setQFilterNome] = useState('');
  const [qFilterLocal, setQFilterLocal] = useState('');
  const [selectedOfficerDetails, setSelectedOfficerDetails] = useState(null);

  const [selectedMonthTab, setSelectedMonthTab] = useState('');
  const [selectedDateTab, setSelectedDateTab] = useState('');

  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState(null);
  const [selectedEscolha, setSelectedEscolha] = useState(null);

  // NOVO: Extrai a lista de pelotões do perfil do usuário para o Dropdown
  const pelotoesList = useMemo(() => {
    let ids = userProfile?.pelotoes_ids || [];
    let nomes = userProfile?.pelotoes_nomes || [];
    
    if (ids.length === 0 && userProfile?.pelotao_id) {
        ids = [userProfile.pelotao_id];
        nomes = [userProfile.pelotao_nome || 'Meu Pelotão'];
    }
    
    return ids.map((id, index) => ({
        id,
        nome: nomes[index] || `Pelotão ${index + 1}`
    }));
  }, [userProfile]);

  useEffect(() => {
    if (pelotoesList.length === 0) {
      setLoading(false);
      return;
    }

    const safeIds = pelotoesList.map(p => p.id).slice(0, 10);

    const qEscolhas = query(
        collection(db, paths.escolhas), 
        where("pelotao_id", "in", safeIds)
    );
    const unsubEscolhas = onSnapshot(qEscolhas, (snap) => {
        setEscolhas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const qRelatorios = query(collection(db, paths.relatoriosEstagio)); 
    const unsubRelatorios = onSnapshot(qRelatorios, (snap) => {
        setRelatorios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const qQueimogramas = collection(db, paths.queimogramas || "queimogramas");
    const unsubQueimogramas = onSnapshot(qQueimogramas, (snap) => {
        setQueimogramas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubLocais = onSnapshot(collection(db, paths.locais), (snap) => {
        setAllLocais(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    setLoading(false);

    return () => { 
        unsubEscolhas(); 
        unsubRelatorios(); 
        unsubLocais(); 
        unsubQueimogramas();
    };
  }, [pelotoesList]);

  // NOVO: Filtrar as escolhas baseadas no dropdown selecionado
  const escolhasFiltradas = useMemo(() => {
      if (filtroPelotao === 'todos') return escolhas;
      return escolhas.filter(e => e.pelotao_id === filtroPelotao);
  }, [escolhas, filtroPelotao]);

  // NOVO: Filtrar os queimogramas baseados no dropdown selecionado
  const queimogramasFiltrados = useMemo(() => {
      if (filtroPelotao === 'todos') return queimogramas;
      return queimogramas.filter(q => {
          const escolhaOrigem = escolhas.find(e => e.id === q.escolha_id);
          return escolhaOrigem && escolhaOrigem.pelotao_id === filtroPelotao;
      });
  }, [queimogramas, escolhas, filtroPelotao]);

  const relatoriosMap = useMemo(() => {
      const map = {};
      relatorios.forEach(r => { map[r.escolha_id] = r; });
      return map;
  }, [relatorios]);

  // --- LÓGICA DE PROCESSAMENTO DO QUEIMOGRAMA ---
  const dadosQueimogramaAgrupados = useMemo(() => {
      const agrupado = {};

      // Usar a lista FILTRADA de queimogramas
      queimogramasFiltrados.forEach(q => {
          const relatorio = relatoriosMap[q.escolha_id];
          const escolha = escolhasFiltradas.find(e => e.id === q.escolha_id);
          
          let nomeOficial = relatorio?.cadete?.qra_preceptor || relatorio?.preceptor?.posto_nome || "Oficial Não Identificado";
          nomeOficial = nomeOficial.trim().toUpperCase();

          let nomeLocal = "Local N/D";
          if (q.local_id) {
              const localObj = allLocais.find(l => l.id === q.local_id);
              if (localObj) nomeLocal = localObj.nome;
          } else if (escolha) {
              nomeLocal = escolha.local_nome;
          }

          const key = `${nomeOficial}-${nomeLocal}`;

          if (!agrupado[key]) {
              agrupado[key] = {
                  oficial: nomeOficial,
                  local: nomeLocal,
                  totalAvaliacoes: 0,
                  somaNotas: {},
                  comentarios: []
              };
              queimogramaQuestions.forEach(qst => agrupado[key].somaNotas[qst.name] = 0);
          }

          const entry = agrupado[key];
          entry.totalAvaliacoes += 1;

          if (q.avaliacao) {
              queimogramaQuestions.forEach(qst => {
                  const valor = q.avaliacao[qst.name];
                  if (typeof valor === 'number') {
                      entry.somaNotas[qst.name] += valor;
                  }
              });

              if (q.avaliacao.informacoes_complementares && q.avaliacao.informacoes_complementares.trim()) {
                  entry.comentarios.push(q.avaliacao.informacoes_complementares);
              }
          }
      });

      return Object.values(agrupado).map(item => {
          const medias = {};
          
          queimogramaQuestions.forEach(qst => {
              const mediaQuesito = item.somaNotas[qst.name] / item.totalAvaliacoes;
              medias[qst.name] = mediaQuesito;
          });

          return {
              ...item,
              medias,
              mediaFinal: medias['nota_geral'] || 0
          };
      }).sort((a, b) => b.mediaFinal - a.mediaFinal);

  }, [queimogramasFiltrados, relatoriosMap, escolhasFiltradas, allLocais]);

  const queimogramaFiltrado = useMemo(() => {
      return dadosQueimogramaAgrupados.filter(item => {
          const matchNome = qFilterNome ? item.oficial.toLowerCase().includes(qFilterNome.toLowerCase()) : true;
          const matchLocal = qFilterLocal ? item.local === qFilterLocal : true;
          return matchNome && matchLocal;
      });
  }, [dadosQueimogramaAgrupados, qFilterNome, qFilterLocal]);


  const diasAgrupados = useMemo(() => {
      const grupos = {};
      
      // Usar a lista FILTRADA de escolhas
      escolhasFiltradas.forEach(escolha => {
          if (escolha.opcao_impossibilidade || !escolha.data) return;
          
          const dataStr = getNormalizedDateString(escolha.data);
          if (!grupos[dataStr]) {
              grupos[dataStr] = {
                  dataStr: dataStr,
                  data: escolha.data,
                  dataDisplay: formatarData(escolha.data),
                  itens: []
              };
          }
          
          const relatorio = relatoriosMap[escolha.id];
          const preceptorInfo = {
              nome: relatorio?.preceptor?.posto_nome || 'Aguardando Relatório',
              telefone: relatorio?.preceptor?.telefone || 'N/D'
          };

          if (preceptorInfo.nome === 'Aguardando Relatório' && relatorio?.cadete?.qra_preceptor) {
              preceptorInfo.nome = relatorio.cadete.qra_preceptor;
              preceptorInfo.telefone = relatorio.cadete.telefone_preceptor || 'N/D';
          }

          grupos[dataStr].itens.push({
              ...escolha,
              preceptor: preceptorInfo,
              relatorioId: relatorio?.id
          });
      });

      const diasOrdenados = Object.values(grupos).sort((a, b) => a.data.toDate() - b.data.toDate());

      diasOrdenados.forEach(dia => {
          dia.itens.sort((a, b) => {
              if (a.tipo === b.tipo) {
                  return a.cadete_nome.localeCompare(b.cadete_nome);
              }
              return a.tipo === 'PCO' ? -1 : 1;
          });
      });

      return diasOrdenados;
  }, [escolhasFiltradas, relatoriosMap]);

  const mesesAgrupados = useMemo(() => {
      const meses = {};
      diasAgrupados.forEach(dia => {
          const data = dia.data.toDate();
          const mesKey = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
          const mesNome = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
          if (!meses[mesKey]) { meses[mesKey] = { key: mesKey, nome: mesNome, dias: [] }; }
          meses[mesKey].dias.push(dia);
      });
      return Object.values(meses).sort((a, b) => a.key.localeCompare(b.key));
  }, [diasAgrupados]);

  useEffect(() => {
      if (mesesAgrupados.length > 0 && !selectedMonthTab) { setSelectedMonthTab(mesesAgrupados[0].key); }
  }, [mesesAgrupados, selectedMonthTab]);

  useEffect(() => {
      if (selectedMonthTab) {
          const mesAtual = mesesAgrupados.find(m => m.key === selectedMonthTab);
          if (mesAtual && mesAtual.dias.length > 0) {
              const diaAtualValido = mesAtual.dias.some(d => d.dataStr === selectedDateTab);
              if (!diaAtualValido) { setSelectedDateTab(mesAtual.dias[0].dataStr); }
          }
      }
  }, [selectedMonthTab, mesesAgrupados, selectedDateTab]);

  const mapData = useMemo(() => {
      if (!selectedDateTab || !diasAgrupados.length) return [];
      const diaAtual = diasAgrupados.find(d => d.dataStr === selectedDateTab);
      if (!diaAtual) return [];
      const groupedByLocation = {};
      diaAtual.itens.forEach(item => {
          const localId = item.local_id;
          const localInfo = allLocais.find(l => l.id === localId);
          if (localInfo && localInfo.latitude && localInfo.longitude) {
              if (!groupedByLocation[localId]) {
                  groupedByLocation[localId] = { ...localInfo, data_escolha: item.data, tipo_escolha: item.tipo, cadetes: [] };
              }
              groupedByLocation[localId].cadetes.push(item.cadete_nome);
          }
      });
      return Object.values(groupedByLocation);
  }, [selectedDateTab, diasAgrupados, allLocais]);

  const handleViewReport = (item) => {
      const relatorio = relatoriosMap[item.id];
      if (relatorio) { setSelectedReportData(relatorio); setSelectedEscolha(item); setShowReportModal(true); } else { alert("O Cadete ainda não iniciou o preenchimento deste relatório."); }
  };

  const RenderRelatorioLeitura = ({ data, escolha }) => {
      if (!data) return null;
      const p = data.preceptor || {}; const c = data.cadete || {};
      return (
          <div className="space-y-6 p-2 text-sm text-gray-800">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                      <div><p className="text-xs text-gray-500 uppercase font-bold">Cadete</p><p className="font-medium">{data.cadete_nome}</p></div>
                      <div><p className="text-xs text-gray-500 uppercase font-bold">Local / Data</p><p className="font-medium">{data.local_nome} - {formatarData(data.data_estagio)}</p></div>
                      <div><p className="text-xs text-gray-500 uppercase font-bold">Período</p><p className="font-medium uppercase">{c.periodo || escolha?.turno || 'N/D'}</p></div>
                      <div><p className="text-xs text-gray-500 uppercase font-bold">Função Executada</p><p className="font-medium">{p.funcao_executada || 'N/D'}</p></div>
                  </div>
              </div>
              <div className="border-t pt-4">
                  <h4 className="text-lg font-bold text-blue-800 mb-3">Relatório do Aluno Oficial</h4>
                  <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4"><div><p className="font-bold text-gray-700 text-xs">Modelo VTR:</p><p className="bg-gray-50 p-2 rounded border">{c.modelo_vtr || '-'}</p></div><div><p className="font-bold text-gray-700 text-xs">Prefixo:</p><p className="bg-gray-50 p-2 rounded border">{c.prefixo || '-'}</p></div></div>
                      <div><p className="font-bold text-gray-700 text-xs mb-1">Área de Conhecimento / Justificativa:</p><p className="bg-gray-50 p-2 rounded border"><span className="font-bold">{c.area_conhecimento || 'N/A'}</span> - {c.justificativa_area || '-'}</p></div>
                      <div><p className="font-bold text-gray-700 text-xs mb-1">Acontecimento Importante:</p><p className="italic bg-gray-50 p-2 rounded border">{c.acontecimento_importante || 'Nenhum relato.'}</p></div>
                      <div><p className="font-bold text-gray-700 text-xs mb-1">Correspondência com Ensino APMBB:</p><p className="italic bg-gray-50 p-2 rounded border">{c.indicacao_preceptor || 'Nenhum relato.'}</p></div>
                      <div><p className="font-bold text-gray-700 text-xs mb-1">Dificuldades e Soluções:</p><p className="italic bg-gray-50 p-2 rounded border">{c.dificuldades_solucoes || 'Nenhum relato.'}</p></div>
                  </div>
              </div>
              <div className="border-t pt-4">
                  <h4 className="text-lg font-bold text-blue-800 mb-3">Avaliação do Oficial Preceptor</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4"><div><p className="font-bold text-gray-700">Oficial Avaliador:</p><p>{p.posto_nome || 'Aguardando...'}</p></div><div><p className="font-bold text-gray-700">Contato:</p><p>{p.telefone || 'N/A'}</p></div></div>
                  {p.nota_geral ? (<div className="space-y-3"><div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 bg-white p-3 rounded border">{ratingQuestions.map((q) => (<div key={q.name} className="flex justify-between items-center border-b border-gray-100 pb-1 last:border-0"><span className="text-gray-600">{q.label}</span><span className="font-bold text-blue-600">{p[q.name] ?? '-'}</span></div>))}</div><div className="flex items-center justify-between border-t border-blue-200 pt-4 mt-4"><span className="text-xl font-bold text-gray-800">NOTA FINAL:</span><span className="text-4xl font-extrabold text-blue-700">{p.nota_geral}</span></div></div>) : (<p className="italic text-orange-600 bg-orange-50 p-2 rounded border border-orange-200">Avaliação ainda não preenchida pelo Preceptor.</p>)}
              </div>
          </div>
      );
  };

  if (loading) return <LoadingSpinner />;
  const mesSelecionadoObj = mesesAgrupados.find(m => m.key === selectedMonthTab);

  return (
    <div className="min-h-screen bg-gray-50 pb-10 -m-4">
      <header className="bg-white shadow-sm sticky top-0 z-10 mb-6">
          <div className="w-full px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
                <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão" className="h-12 w-12" />
                <div>
                    <h1 className="text-4xl font-bold text-gray-800">Comandante de Pelotão</h1>
                    <p className="text-sm text-gray-600">{userProfile.nome}</p>
                </div>
            </div>

            {/* CAIXA DE SELEÇÃO: Renderiza apenas se tiver mais de um pelotão */}
            {pelotoesList.length > 1 && (
                <div className="flex items-center space-x-3 bg-gray-50 px-4 py-2 rounded-xl border border-gray-200 shadow-sm">
                    <label className="text-sm font-bold text-gray-600 uppercase tracking-wider">Visualizar:</label>
                    <select 
                        value={filtroPelotao} 
                        onChange={(e) => setFiltroPelotao(e.target.value)}
                        className="bg-white border border-gray-300 rounded-lg text-sm font-bold text-blue-700 py-1.5 px-3 focus:ring-blue-500 focus:border-blue-500 cursor-pointer shadow-sm"
                    >
                        <option value="todos">TODOS OS PELOTÕES</option>
                        {pelotoesList.map(p => (
                            <option key={p.id} value={p.id}>{p.nome}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* SELO SIMPLES: Renderiza se o Cmt tiver apenas 1 pelotão */}
            {pelotoesList.length === 1 && (
                <div className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-xl border border-blue-100 font-bold text-sm shadow-sm">
                    {pelotoesList[0].nome}
                </div>
            )}
          </div>
      </header>

      <main className="w-full px-6">
        
        {/* Navegação de Abas */}
        <div className="flex justify-start mb-6 overflow-x-auto pb-2">
            <div className="flex space-x-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-200">
                <button onClick={() => setActiveTab('escala')} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'escala' ? 'bg-blue-600 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}><ListIcon /> Escala e Relatórios</button>
                <button onClick={() => setActiveTab('mapa')} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'mapa' ? 'bg-blue-600 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}><MapIcon /> Mapa Operacional</button>
                <button onClick={() => setActiveTab('queimograma')} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'queimograma' ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}><FireIcon /> Avaliação 360º</button>
            </div>
        </div>

        <div className="space-y-4 animate-fade-in">
            {/* CONTEÚDO QUEIMOGRAMA (FORMATO TABELA) */}
            {activeTab === 'queimograma' && (
                <div className="space-y-4">
                    {/* Filtros */}
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex-1 w-full">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Buscar Oficial (Nome/Posto)</label>
                            <input 
                                type="text" 
                                value={qFilterNome} 
                                onChange={(e) => setQFilterNome(e.target.value)} 
                                placeholder="Ex: Cap Silva" 
                                className="w-full border p-2 rounded text-sm" 
                            />
                        </div>
                        <div className="flex-1 w-full">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Filtrar por Unidade</label>
                            <select 
                                value={qFilterLocal} 
                                onChange={(e) => setQFilterLocal(e.target.value)} 
                                className="w-full border p-2 rounded text-sm bg-white"
                            >
                                <option value="">Todas as Unidades</option>
                                {[...new Set(dadosQueimogramaAgrupados.map(i => i.local))].sort().map(local => (
                                    <option key={local} value={local}>{local}</option>
                                ))}
                            </select>
                        </div>
                        <Button onClick={() => {setQFilterNome(''); setQFilterLocal('')}} variant="secondary" className="h-10">Limpar</Button>
                    </div>

                    {/* Tabela de Resultados */}
                    <div className="bg-white shadow rounded-lg overflow-x-auto border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oficial</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidade</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Avaliações</th>
                                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Média Geral</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {queimogramaFiltrado.length === 0 ? (
                                    <tr><td colSpan="5" className="px-6 py-4 text-center text-gray-500">Nenhuma avaliação encontrada com os filtros atuais.</td></tr>
                                ) : (
                                    queimogramaFiltrado.map((item, idx) => {
                                        const nota = item.mediaFinal.toFixed(1);
                                        let badgeColor = 'bg-gray-100 text-gray-800';
                                        if (item.mediaFinal >= 9) badgeColor = 'bg-green-100 text-green-800';
                                        else if (item.mediaFinal >= 7) badgeColor = 'bg-blue-100 text-blue-800';
                                        else badgeColor = 'bg-red-100 text-red-800';

                                        return (
                                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{item.oficial}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.local}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium text-gray-700">{item.totalAvaliacoes}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                                    <span className={`px-3 py-1 inline-flex text-sm leading-5 font-bold rounded-full ${badgeColor}`}>
                                                        {nota}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button 
                                                        onClick={() => setSelectedOfficerDetails(item)}
                                                        className="text-blue-600 hover:text-blue-900 flex items-center justify-end gap-1 ml-auto transition-colors"
                                                    >
                                                        <EyeIcon /> Detalhes
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* CONTEÚDO ORIGINAL (ESCALA) */}
            {diasAgrupados.length === 0 && activeTab !== 'queimograma' ? (
                <p className="text-gray-500 text-center py-8 bg-white rounded-lg shadow">Nenhum estágio agendado para o pelotão selecionado.</p>
            ) : (
                <>
                    {activeTab !== 'queimograma' && (
                        <>
                            <div className="mb-2">
                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Mês de Referência:</span>
                                <div className="flex overflow-x-auto space-x-2 pb-2 mt-1 scrollbar-hide">
                                    {mesesAgrupados.map((mes) => (
                                        <button
                                            key={mes.key}
                                            onClick={() => setSelectedMonthTab(mes.key)}
                                            className={`px-4 py-1.5 rounded-lg text-sm font-bold whitespace-nowrap transition-colors border ${
                                                selectedMonthTab === mes.key
                                                    ? 'bg-gray-800 text-white border-gray-800 shadow'
                                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            {mes.nome.toUpperCase()}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mb-4">
                                <div className="flex overflow-x-auto space-x-2 pb-2 scrollbar-hide">
                                    {mesSelecionadoObj && mesSelecionadoObj.dias.map((dia) => (
                                        <button
                                            key={dia.dataStr}
                                            onClick={() => setSelectedDateTab(dia.dataStr)}
                                            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors border ${
                                                selectedDateTab === dia.dataStr
                                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50 hover:border-gray-400'
                                            }`}
                                        >
                                            {dia.dataDisplay}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'escala' && (
                        <>
                            {diasAgrupados.map((dia) => {
                                if (dia.dataStr !== selectedDateTab) return null;
                                return (
                                    <div key={dia.dataStr} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden animate-fade-in">
                                        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100 flex justify-between items-center">
                                            <h3 className="font-bold text-blue-800 flex items-center gap-2 text-lg">
                                                <CalendarIcon /> Escala do dia {dia.dataDisplay}
                                            </h3>
                                            <span className="text-xs font-bold bg-blue-200 text-blue-800 px-2 py-1 rounded shadow-sm">
                                                {dia.itens.length} Cadetes
                                            </span>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-100">
                                                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-semibold">Cadete</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Tipo / Local / Turno</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Oficial Preceptor</th>
                                                        <th className="px-4 py-3 text-left font-semibold">Contato</th>
                                                        <th className="px-4 py-3 text-center font-semibold">Relatório</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {dia.itens.map(item => {
                                                        const rel = relatoriosMap[item.id]; 
                                                        const isPJ = item.tipo === 'PJ';
                                                        const rowClass = isPJ ? 'hover:bg-green-50 border-l-4 border-green-500' : 'hover:bg-blue-50 border-l-4 border-blue-500';
                                                        const badgeClass = isPJ ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-blue-100 text-blue-800 border border-blue-200';

                                                        return (
                                                            <tr key={item.id} className={`transition-colors ${rowClass}`}>
                                                                <td className="px-4 py-3 text-sm font-bold text-gray-800">{item.cadete_nome}</td>
                                                                <td className="px-4 py-3 text-sm text-gray-600 flex items-center gap-2">
                                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${badgeClass}`}>{item.tipo}</span>
                                                                    <span>{item.local_nome}</span>
                                                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.turno === 'noite' ? 'bg-indigo-100 text-indigo-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                                        {item.turno ? item.turno.toUpperCase() : 'DIA'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3 text-sm">
                                                                    {item.preceptor.nome !== 'Aguardando Relatório' ? (<span className="text-blue-700 font-medium">{item.preceptor.nome}</span>) : (<span className="text-gray-400 italic text-xs">Não informado</span>)}
                                                                </td>
                                                                <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                                                                    {item.preceptor.telefone}
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <button onClick={() => handleViewReport(item)} disabled={!rel} className={`p-2 rounded-full transition-all ${!rel ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100 cursor-pointer active:scale-95'}`} title={rel ? "Clique para ver o relatório" : "Relatório não iniciado"}>
                                                                        {rel?.preceptor?.posto_nome ? <CheckCircleIcon /> : <FileTextIcon />}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })}
                        </>
                    )}

                    {activeTab === 'mapa' && (
                        <div className="animate-fade-in bg-white p-6 rounded-lg shadow-lg h-full border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <MapIcon /> Distribuição Operacional - {diasAgrupados.find(d => d.dataStr === selectedDateTab)?.dataDisplay}
                            </h3>
                            <div className="w-full">
                                {mapData.length > 0 ? (
                                    <CadeteMap locais={mapData} />
                                ) : (
                                    <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200 text-gray-500">
                                        Nenhum cadete alocado em local com coordenadas cadastradas para este dia.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
      </main>

      <Modal show={showReportModal} onClose={() => setShowReportModal(false)} title="Relatório de Estágio (Visualização)" size="2xl">
          <RenderRelatorioLeitura data={selectedReportData} escolha={selectedEscolha} />
          <div className="flex justify-end pt-4 border-t"><Button onClick={() => setShowReportModal(false)} variant="secondary">Fechar</Button></div>
      </Modal>

      {/* --- MODAL DE DETALHES DO QUEIMOGRAMA --- */}
      <Modal show={!!selectedOfficerDetails} onClose={() => setSelectedOfficerDetails(null)} title={`Detalhes da Avaliação: ${selectedOfficerDetails?.oficial}`} size="2xl">
          {selectedOfficerDetails && (
              <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                          <div><span className="font-bold text-blue-800">Unidade:</span> {selectedOfficerDetails.local}</div>
                          <div><span className="font-bold text-blue-800">Total Avaliações:</span> {selectedOfficerDetails.totalAvaliacoes}</div>
                      </div>
                  </div>

                  <div>
                      <h4 className="font-bold text-gray-800 mb-3 border-b pb-1">Média por Quesito (0-10)</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                          {queimogramaQuestions.filter(q => q.name !== 'nota_geral').map(q => (
                              <div key={q.name} className="flex justify-between items-center text-sm border-b border-gray-100 pb-1">
                                  <span className="text-gray-600">{q.label}</span>
                                  <span className="font-bold text-blue-600">{selectedOfficerDetails.medias[q.name].toFixed(2)}</span>
                              </div>
                          ))}
                      </div>
                  </div>

                  <div>
                      <h4 className="font-bold text-gray-800 mb-3 border-b pb-1">Comentários (Informações Complementares)</h4>
                      {selectedOfficerDetails.comentarios.length === 0 ? (
                          <p className="text-gray-400 italic text-sm">Nenhum comentário registrado.</p>
                      ) : (
                          <ul className="space-y-3 max-h-60 overflow-y-auto pr-2">
                              {selectedOfficerDetails.comentarios.map((comentario, index) => (
                                  <li key={index} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm text-gray-700 italic relative">
                                      <span className="absolute top-2 left-2 text-gray-300 font-serif text-3xl opacity-50">"</span>
                                      <p className="pl-4">{comentario}</p>
                                  </li>
                              ))}
                          </ul>
                      )}
                  </div>

                  <div className="flex justify-end pt-4 border-t">
                      <Button onClick={() => setSelectedOfficerDetails(null)} variant="primary">Fechar</Button>
                  </div>
              </div>
          )}
      </Modal>

    </div>
  );
};